import { useContext, useState, useEffect } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  BoxFull,
  Button,
  Dropdown,
  TransactionalSubheader,
} from "iuvity-ui-components";
import { Header } from "../../components";
import { InputText } from "iuvity-ui-components/dist/inputtext"
import {
  CustomDialog,
  DialogProps,
} from "../../components/dialog/CustomDialog";
import { useMasters } from "../../hooks/useMasters";
import { PointsMasterSchema } from "../../validations/schemas";
import { AppContext } from "../../context/index";
import { ErrorMessage } from "@hookform/error-message";
import { REVALIDATE_FORM_PROPS } from "../../constants";
import { InputTextarea } from "iuvity-ui-components/dist/inputtextarea";
import { Upload } from "iuvity-ui-components/dist/upload";

interface NewMasterPointsProps {
  update?: boolean;
}

const formBlankValues = {
  code: "",
  pointServiceImage: "",
  name: "",
  description: "",
  layerName: "",
};

export const NewMasterPointsPage: React.FC<NewMasterPointsProps> = ({
  update = false,
}) => {
  const navigate = useNavigate();
  const {
    t,
    appState: { user, selectedPointsObject },
    setAppState,
  } = useContext(AppContext);

  const {
    control,
    watch,
    getValues,
    setValue,
    formState: { errors, isDirty, isValid },
  } = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: update
        ? {
            name: selectedPointsObject?.name,
            description: selectedPointsObject?.description,
            code: selectedPointsObject?.code,
            pointServiceImage: '',
            layerName: selectedPointsObject?.image?.layerName,
          }
        : formBlankValues,
  });

  const nameValues = useWatch({
    control: control,
    name: "name",
  });

  const descriptionValues = useWatch({
    control: control,
    name: "description",
  });

  const codeValues = useWatch({
    control: control,
    name: "code",
  });

  const pointServiceImageValues = useWatch({
    control: control,
    name: "pointServiceImage",
  });

  const { masterAction } = useMasters("ID");
  const { dataMaster } = useMasters("DATA")

  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  const onReturn = () => {
    setAppState((prev) => ({
      ...prev,
      selectedPointsObject: null,
    }));
    navigate(-1);
    window.scrollTo(0, 0);
  };

  const onAction = async () => {
    const formValues = getValues();
    const actionResult = await masterAction(update ? "UPDATE" : "CREATE",
    update ? {...formValues, id: selectedPointsObject.id} : formValues);
    if (actionResult === "SUCCESS") {
      setAppState((prev) => ({
        ...prev,
        activeIndexPoints: 4,
    }));
      navigate("/pointsofattention");
      window.scrollTo(0, 0);
    }
  };

  const handleModalProps = (actionType: "action" | "cancel", goHome?: boolean) => {
    switch (actionType) {
      case "action":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t(`PointsOfAttention.${update ? "save" : "create"}ModalTitle`),
          content: t(`PointsOfAttention.${update ? "save" : "create"}MasterModalContent`),
          onConfirm: onAction,
        }));
      case "cancel":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t("Users.cancelModalTitle"),
          content: t("Users.cancelModalContent"),
          onConfirm: goHome ? () => navigate('/') : onReturn,
        }));
      default:
        return;
    }
  };

  const [layerName, setLayerName] = useState<string>(update ? selectedPointsObject?.image?.layerName : '')
  const [layerNameValidator,setLayerNameValidator] = useState<string>(update ? selectedPointsObject?.image?.layerName : '')
  const [hasImageError,setHasImageError] = useState<boolean>(true)
  const invoiceUploadHandler = ({ files }: any) => {
    setHasImageError(false);
    setValue('pointServiceImage', files[0], REVALIDATE_FORM_PROPS);
    const splitLayerName = files[0].name.split('.')
    const formatLayerName = `${splitLayerName[0].slice(0,30)}.${splitLayerName[splitLayerName.length - 1]}`;
    setLayerNameValidator(splitLayerName[0].slice(0,30));
    setValue('layerName', splitLayerName[0].slice(0,30));
    setLayerName(formatLayerName);
  }
  
  const invoiceRemoveHandler = () => {
    setValue('pointServiceImage', '', REVALIDATE_FORM_PROPS);
    setValue('layerName', update ? selectedPointsObject?.image?.layerName : '', REVALIDATE_FORM_PROPS);
    setLayerName(update ? selectedPointsObject?.image?.layerName : '');
    setLayerNameValidator(update ? selectedPointsObject?.image?.layerName : '');
  }

  // EFECTO PARA CONTROLAR CARACTERES NO PERMITIDOS SIN REGEX
  useEffect(()=>{
    watch((value)=>{
      if (
        value.name.includes('^')
        || value.name.includes('`')
        || value.name.includes('¨')
        || value.name.includes('~')
      ) {
        setValue('name', value.name.replace('^', '').replace('`', '').replace('¨', '').replace('~', ''))
      }
      if (
        value.code.includes('´')
        || value.code.includes('^')
        || value.code.includes('`')
        || value.code.includes('¨')
        || value.code.includes('~')
      ) {
        setValue('code', value.code.replace('´', '').replace('^', '').replace('`', '').replace('¨', '').replace('~', ''))
      }
      if (
        value.description.includes('^')
        || value.description.includes('`')
        || value.description.includes('¨')
        || value.description.includes('~')
      ) {
        setValue('description', value.description.replace('^', '').replace('`', '').replace('¨', '').replace('~', ''))
      }
    })
  },[watch])

  useEffect(()=>{
      setHasImageError(false)
      if (layerNameValidator !== '' && !(/^([a-zA-Z0-9\\sÁÉÍÓÚáéíóúÑñ_]+)$/.test(layerNameValidator))) {
        setHasImageError(true)
      }
      layerNameValidator === '' && setHasImageError(true);
  },[layerNameValidator])

  return (
    <>
      {/* <Header
        welcomeMessage={t("Dashboard.welcomeTitle")}
        userName={user?.name}
      /> */}

      <TransactionalSubheader
        subheaderTitle={
          <div style={{ position: "relative", left: "-40px" }}>
            <i
              className="iu iu-icon-left pr-3"
              onClick={() => isDirty ? handleModalProps("cancel") : onReturn()}
              aria-hidden="true"
              style={{
                fontSize: "var(--font-size-tablet-title)",
                cursor: "pointer",
              }}
            />
            {update ? t("PointsOfAttention.editMasterTitle") : t("PointsOfAttention.newMasterTitle")}
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          id={t("MappedFields.mappedFieldsTitle")}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => isDirty ? handleModalProps("cancel", true) : navigate('/'),
          }}
          breadcrumbsItems={[
            {
              label: t("PointsOfAttention.pointsTitle"),
              command: () => isDirty ? handleModalProps("cancel") : onReturn(),
            },
            {
              label: t(`PointsOfAttention.${update ? 'edit' : 'create'}MasterBreadcrumb`),
              style: { fontFamily: "var(--fontBarlowSemiBold)" },
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
      </TransactionalSubheader>

      <BoxFull
        className="flex align-items-center justify-content-center my-4"
        variant="tertiary"
      >
        <div style={{
          display: 'flex',
          flexDirection: 'column',
        }}>
          <p style={{ marginBottom: 'var(--space-base)' }}>{
            t(`PointsOfAttention.${update ? 'edit' : 'create'}NewMasterSubtitle`)}
          </p>
          <div className="form-container md py-5 w-full">
            <div className="form md w-full">
              <div className="flex align-items-flex-start justify-content-flex-start flex-wrap">
                {Object.keys(formBlankValues)
                  .map((key, index) => (
                    key !== 'layerName' &&
                    <div
                      key={`c-${key}`}
                      className="flex flex-column mb-3 px-6"
                      style={{ width: "50%" }}
                    >
                      <Controller
                        name={key as any}
                        control={control}
                        rules={(PointsMasterSchema(t) as any)[key]}
                        render={({ field }) => {
                          field.onChange = (e) => {
                            setValue(field.name, e.target.value, REVALIDATE_FORM_PROPS)
                          };
                          const extraProps = {
                            keyfilter: (PointsMasterSchema(t) as any)[key]
                              ?.keyfilter,
                          };
                          return ['description'].includes(key) ? (
                            <div className='pointsofattention-fields' style={{ paddingLeft: '4rem' }}>
                              <InputTextarea
                                key={key}
                                id={`input-label-${key}`}
                                label={t(`PointsOfAttention.${key}Label`)}
                                placeholder={t(`PointsOfAttention.${key}Placeholder`)}
                                maxLength={(PointsMasterSchema(t)as any)[key]?.maxLength?.value}
                                autoComplete="off"
                                className={
                                  // @ts-ignore
                                  errors[key]?.message ? "p-invalid" : "mb-4"
                                }
                                {...field}
                                {...extraProps}
                                cols={32}
                                autoResize
                              />
                            </div>
                          ): ['pointServiceImage'].includes(key) ? (
                            <div className='pointsofattention-fields' style={{ paddingLeft: '4rem' }}>
                              <div className="label-container"><label htmlFor="input-label-image" className="block ">{t(`PointsOfAttention.${key}Label`)}</label></div>
                              <div className="flex align-items-center gap-2 mt-1">
                                {update && getValues('pointServiceImage') === '' &&
                                  <p>{ layerName }</p>
                                }
                                  <Upload
                                  chooseLabel={
                                    getValues('pointServiceImage') !== ''
                                      ? layerName
                                      : t("Applications.uploadBtnLabel")
                                  }
                                  mode="basic"
                                  accept="image/*"
                                  customUpload={true}
                                  onClear={invoiceRemoveHandler}
                                  uploadHandler={invoiceUploadHandler}
                                  auto={true}
                                  disabled={getValues('pointServiceImage') !== ''}
                                />
                              </div>
                            </div>                            
                          ): (
                            <div className='pointsofattention-fields' style={{ paddingLeft: '4rem' }}>
                              <InputText
                                key={key}
                                id={`input-label-${key}`}
                                label={t(`PointsOfAttention.${key}Label`)}
                                placeholder={t(`PointsOfAttention.${key}Placeholder`)}
                                maxLength={(PointsMasterSchema(t)as any)[key]?.maxLength?.value}
                                type="text"
                                autoComplete="off"
                                className={
                                  // @ts-ignore
                                  errors[key]?.message ? "p-invalid" : "mb-4"
                                }
                                {...field}
                                {...extraProps}
                                disabled={key === 'pointServiceImage'}
                                isReadOnly={update && key === 'code'}
                              />
                            </div>
                          )
                          ;
                        }}
                      />
                      {
                        key === 'pointServiceImage' ?
                          hasImageError
                          ?
                            <small
                              key={`small-${key}`}
                              className="p-error block"
                              style={{
                                marginTop: "0.5rem",
                                marginBottom: "1.5rem",
                                paddingLeft: '4rem',
                              }}
                            >
                              {
                                layerNameValidator === ''
                                ? ''
                                : t("GlobalConfigurations.generalErrorMessage")
                              }
                            </small>
                          : null
                        :
                        <ErrorMessage
                          errors={errors}
                          name={key as any}
                          //@ts-ignore
                          render={({ message }) => (
                            <small
                              key={`small-${key}`}
                              className="p-error block"
                              style={{
                                marginTop: "0.5rem",
                                marginBottom: "1.5rem",
                                paddingLeft: '4rem',
                              }}
                            >
                              {message}
                            </small>
                          )}
                        />
                      }
                    </div>
                  ))}
                </div>
            </div>
          </div>
        </div>
      </BoxFull>

      <BoxFull className="my-5 p-0" style={{ backgroundColor: "transparent" }}>
        <div className="flex align-items-center justify-content-end gap-5">
          <Button
            variant="p-button-link"
            label={t("PointsOfAttention.cancelBtnLabel")}
            onClick={isDirty ? () => handleModalProps("cancel") : onReturn}
          />
          <Button
            variant="p-button"
            label={
              update
                ? t("PointsOfAttention.saveBtnLabel")
                : t("PointsOfAttention.saveBtnLabel")
            }
            disabled={
              update ?
              !(
               (isDirty ||
               layerName !== selectedPointsObject?.image?.layerName) &&
               !hasImageError
              )
              :
              !(
                nameValues?.length > 0 &&
                descriptionValues?.length > 0 &&
                codeValues?.length > 0 &&
                pointServiceImageValues !== '' &&
                !hasImageError &&
                isValid
               )
            }
            onClick={() => handleModalProps("action")}
          />
        </div>
      </BoxFull>

      <CustomDialog
        title={modalProps.title}
        visible={modalProps.visible}
        content={modalProps.content}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
